import { useEffect, useState } from 'react';
import { setPartnerKey } from './analytics';

/**
 * Small store around the portal config/token we keep in localStorage.
 *
 * The navbar used to read `localStorage.getItem('portalConfig')` straight
 * inside render, so the partner's name and logo only appeared after a route
 * change — on first load of the marketplace the config arrives *after* the
 * navbar has mounted and nothing told it to re-render. Subscribers fix that.
 *
 * It also centralises the JSON.parse: an unparseable value used to throw
 * during render and take the whole app down with a blank screen.
 */

const CONFIG_KEY = 'portalConfig';
const listeners = new Set();

const safeParse = (raw) => {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const readPortalConfig = () => safeParse(localStorage.getItem(CONFIG_KEY));

export const writePortalConfig = (config) => {
  try {
    localStorage.setItem(CONFIG_KEY, JSON.stringify(config));
  } catch {
    /* private mode / quota — the in-memory notify below still works */
  }
  // Every config load passes through here, so this is where analytics learns
  // the partner's canonical key.
  setPartnerKey(config?.partner_key);
  listeners.forEach((fn) => fn(config));
};

export const clearPortalConfig = () => {
  localStorage.removeItem(CONFIG_KEY);
  listeners.forEach((fn) => fn(null));
};

export const usePortalConfig = () => {
  const [config, setConfig] = useState(readPortalConfig);

  useEffect(() => {
    const onChange = (next) => setConfig(next);
    listeners.add(onChange);

    // Another tab writing the config should update this one too.
    const onStorage = (e) => {
      if (e.key === CONFIG_KEY) setConfig(safeParse(e.newValue));
    };
    window.addEventListener('storage', onStorage);

    return () => {
      listeners.delete(onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  return config;
};

/** The item the user tapped through to a detail page. */
export const readSelectedItem = () => safeParse(localStorage.getItem('selectedItem'));

export const writeSelectedItem = (item) => {
  try {
    localStorage.setItem('selectedItem', JSON.stringify(item));
  } catch {
    /* ignore */
  }
};
